import React from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import { undoWithSuppression } from "../../../../redux/actions/history";
import { colors } from "../../../../utils";
import { AUTODERIVE_TOAST_MESSAGE } from "./autoDerivePaletteLogic";

// D18 safety-net toast: when a Segment's palette auto-changes because its
// environment/fill image changed (no explicit palette click), surface it with a
// one-tap Undo. Reuses react-toastify (the app's existing toast) and the shared
// `undo` history action — no new toast system, no new undo path.

const wrapStyle = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  fontFamily: "'DM Sans', sans-serif",
  color: "#123051", // navy ink
  fontSize: 14,
};

// Undo is the ONE control guarding an un-clicked color change — it needs a real
// keyboard focus ring and a >=32px (desktop-web) hit area, neither of which an
// inline style object can express (no :focus-visible). Hence a styled-component.
const UndoButton = styled.button`
  border: none;
  background: transparent;
  color: ${colors.PrimaryPurple};
  font-weight: 600;
  font-family: "DM Sans", sans-serif;
  font-size: 14px;
  cursor: pointer;
  min-height: 32px;
  padding: 6px 10px;
  /* T22.290: the longer, self-explaining toast copy must never squeeze the
     one control into a broken "Un / do" two-line word. */
  white-space: nowrap;
  flex-shrink: 0;
  border-radius: 6px;

  &:hover {
    background: ${colors.PrimaryPurple100 || "rgba(155, 90, 248, 0.1)"};
  }

  &:focus-visible {
    outline: 2px solid ${colors.PrimaryPurple700};
    outline-offset: 1px;
  }
`;

// The message baked with an explicit Undo control. `dispatch` + `closeToast` are
// injected so this stays a pure presentational component.
//
// Bug 16.1 finding B (gauntlet-builder2, 2026-08-06, independent critic): this button dispatched
// undo() RAW — the SAME re-hydration-echo bug the toolbar Undo button had, just on a second entry
// point the first fix pass missed. Now goes through the centralized undoWithSuppression() thunk
// (redux/actions/history.js) every real undo/redo trigger must use.
export function AutoDeriveUndoToast({ dispatch, closeToast }) {
  const [label, action] = AUTODERIVE_TOAST_MESSAGE.split("—").map((s) =>
    s.trim()
  );
  return (
    <div style={wrapStyle} role="status">
      <span>{label}</span>
      <UndoButton
        type="button"
        title={action || "Undo"}
        onClick={() => {
          dispatch(undoWithSuppression());
          if (typeof closeToast === "function") closeToast();
        }}
        aria-label={`${action} segment color change`}
      >
        {action || "Undo"}
      </UndoButton>
    </div>
  );
}

// A fixed toastId (rather than react-toastify's default auto-generated one) so
// a second fill-replace fired before the first toast's 6s autoClose (or a
// segment-switch racing an in-flight derive) UPDATES the same toast instead of
// stacking a duplicate underneath it — gordon-0920-editwheel-open-mutates-P0's
// "stacks two deep" defect. react-toastify no-ops a `toast()` call whose
// `toastId` is already active, so this is a de-dupe, not a suppression: a
// genuinely new derive still shows (and resets the 6s clock).
export const AUTO_DERIVE_TOAST_ID = "segment-palette-auto-derive-undo";

// Fire the toast. Kept as a helper so callers (the palette-derive hook) need only
// a dispatch reference.
export function showAutoDeriveUndoToast(dispatch) {
  toast(
    ({ closeToast }) => (
      <AutoDeriveUndoToast dispatch={dispatch} closeToast={closeToast} />
    ),
    { autoClose: 6000, toastId: AUTO_DERIVE_TOAST_ID }
  );
}
